import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { ReceiptDTO } from "../../models/ArticleDTO";

const BASE = "/admin/campus/";

export interface ProductRow {
	id: number;
	waCode: string | null;
	kind: string;
	kindLabel: string;
	name: string;
	sku: string;
	legacySku: string | null;
	price: number;
	/** Producto sin precio aprobado: se puede comprar, no vender. */
	pricePending?: boolean;
	/** Precio sugerido esperando a Super Admin. */
	suggestedPrice?: number | null;
	sizeType: string | null;
	sizeValue: string | null;
	/** Color de una variante ("Blanco"); null en modelos y productos sin color. */
	colorValue?: string | null;
	/** Variante con precio propio (no sigue el del modelo). */
	ownPrice?: boolean;
	modelId: number | null;
	variantCount: number;
	/** Solo con groupVariants: los colores y tallas vivos del producto. */
	variantSummary?: VariantSummary | null;
	trackStock: boolean;
	/** Artículo de valor alto: se controla por número de serie. */
	serialized?: boolean;
	requiresSizeAtDelivery: boolean;
	hasPhoto: boolean;
	barcode: string | null;
	onHand: number;
	deprecatedAt: string | null;
	/** Agotado: sigue en el catálogo pero no se vende hasta «Volver a Vender». */
	soldOutAt?: string | null;
	replacedByArticleId: number | null;
	versionLabel: string | null;
	tier: string | null;
}

export interface KitComponentRow {
	id?: number;
	articleId: number;
	name: string;
	waCode: string | null;
	kind: string | null;
	sizeValue: string | null;
	quantity: number;
	optional: boolean;
	sizeAtDelivery: boolean;
	/** Tallas y colores que la pieza trae (el kit los toma solos). null = la pieza no tiene variantes. */
	variantSummary?: VariantSummary | null;
	notes: string | null;
	substitutes: { articleId: number; name: string; waCode: string | null; sizeValue: string | null }[];
}

export interface VariantSummary { count: number; colors: string[]; sizes: string[] }

/** Una variante en la ficha del producto: su combinación, su precio y su existencia. */
export interface ProductVariant {
	id: number; waCode: string | null; name: string; sizeValue: string | null; colorValue: string | null; label: string; barcode: string | null;
	price: number; ownPrice: boolean; pricePending: boolean; suggestedPrice: number | null; deprecatedAt: string | null;
	onHand: number; stockByWarehouse: { warehouseId: number; code: string; name: string; onHand: number }[];
}

export interface DeliveryOption {
	articleId: number;
	name: string;
	waCode: string | null;
	sizeValue: string | null;
	colorValue?: string | null;
	/** "Blanco · M": lo que distingue a la variante (null si no es variante). */
	label?: string | null;
	barcode: string | null;
	isSubstitute: boolean;
	onHand: number;
	/** Pendiente de recibir de órdenes de compra aprobadas hacia esta bodega. */
	inTransit?: number;
	eta?: string | null;
	/** Artículo con número de serie: hay que elegir cuál pieza sale. */
	serialized?: boolean;
	serials?: string[];
}

export interface DeliveryLine {
	componentId: number | null;
	name: string;
	needed: number;
	delivered: number;
	pending: number;
	optional: boolean;
	sizeAtDelivery: boolean;
	/** La variante que pidió el alumno al comprar: la entrega llega con ella elegida. */
	preferredArticleId?: number | null;
	options: DeliveryOption[];
}

export interface DeliveryPlan {
	/** false = producto del catálogo viejo: se entrega sin descontar inventario. */
	tracksInventory?: boolean;
	/** Sold with the old catalog: today's new-catalog products + what was given before, in text. */
	legacy?: boolean;
	paidInFull?: boolean;
	/** Producto del catálogo nuevo que aún no está pagado completo: no se entrega. */
	blockedReason?: string | null;
	kitId: number;
	kitName: string;
	quantity: number;
	/** Lo que pidió el alumno al comprar ("Blanco · M"). */
	preferredLabel?: string | null;
	deliveryStatusId: number;
	warehouse: { id: number; code: string; name: string };
	warehouseOptions?: { id: number; code: string; name: string; kind: string; countryCode: string; campusId: number | null; delivers?: boolean }[];
	lines: DeliveryLine[];
	pendingTotal: number;
	deliveredItems: { id: number; name: string; sizeValue: string | null; quantity: number; author: string; createdAt: string | null }[];
	isTotal?: boolean;
}

/**
 * Motivo real de un error en una descarga (Excel/foto): con responseType "blob" el JSON del error llega dentro del blob.
 * Lee "errors" (validación) o "message" (BadRequest) igual que apiError.
 */
export const blobErrorMessage = async (requestError: any, fallback: string): Promise<string> => {
	let payload = requestError?.response?.data;
	if (typeof Blob !== "undefined" && payload instanceof Blob) {
		try { payload = JSON.parse(await payload.text()); } catch (parseError) { payload = null; }
	}
	const first = payload?.errors ? Object.values(payload.errors)[0] : null;
	if (first) return String(Array.isArray(first) ? first[0] : first);
	if (payload?.message) return String(payload.message);
	if (!requestError?.response) return "Sin conexión: no se pudo descargar. Revisa la señal e inténtalo otra vez.";
	return fallback;
};

/** Productos e inventario (Fase 1, Sep 2026): catálogo, kits compuestos, bodegas y entrega pieza por pieza. */
export class InventoryService {

	private clean(filters: Record<string, any>): Record<string, any> {
		const params: Record<string, any> = {};
		Object.keys(filters || {}).forEach((key) => {
			const value = filters[key];
			if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length < 1)) params[key] = value;
		});
		return params;
	}

	/** Los arreglos viajan como JSON dentro del multipart. */
	private form(data: Record<string, any>, photo?: File | null): FormData {
		const body = new FormData();
		Object.keys(data).forEach((key) => {
			const value = data[key];
			if (value === undefined || value === null) return;
			body.append(key, Array.isArray(value) || typeof value === "object" ? JSON.stringify(value) : String(value));
		});
		if (photo) body.append("photo", photo);
		return body;
	}

	// ---- catálogo

	catalogs = async (campusId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_catalogs`);

	list = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products`, { params: { ...this.clean(filters), page, perPage } });

	get = async (campusId: number, productId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}`);

	create = async (campusId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products`, this.form(data, photo));

	update = async (campusId: number, productId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_update`, this.form(data, photo));

	deprecate = async (campusId: number, productId: number, reason: string, replacedByArticleId?: number | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_deprecate`, { reason, replacedByArticleId: replacedByArticleId || null });

	soldOut = async (campusId: number, productId: number, soldOut: boolean, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_soldOut`, { soldOut, reason });

	restore = async (campusId: number, productId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_restore`, { reason });

	/** Tallas y/o colores: se crea cada combinación. existingColor/existingSize = lo que son las variantes que ya existían sin ese dato. */
	createVariants = async (campusId: number, productId: number, sizes: string[], colors: string[] = [], existingColor?: string | null, existingSize?: string | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_variants`, { sizes, colors, existingColor: existingColor || null, existingSize: existingSize || null });

	/** Precio propio de variantes (price null = vuelven al precio del modelo). Super Admin lo fija; los demás lo sugieren. */
	variantPrices = async (campusId: number, productId: number, variantIds: number[], price: number | null, note?: string): Promise<AxiosResponse<{ message: string }>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_variantPrices`, { variantIds, price, note: note || null });

	photoUrl = (campusId: number, productId: number): string =>
		`${axiosInstance.defaults.baseURL || ""}${BASE}${campusId}/products/${productId}/_photo`;

	/** La foto va tras autenticación: se descarga como blob y se usa como objeto URL. thumb = miniatura de 160 px (listas). */
	// ---- unificar duplicados (Super Admin, 2026-11-03): varios repetidos a un sobreviviente, un solo Deshacer

	mergePreview = async (campusId: number, pairs: { keeperId: number; mergedId: number }[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_mergePreview`, { pairs });

	merge = async (campusId: number, pairs: { keeperId: number; mergedId: number }[], reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_merge`, { pairs, reason });

	mergeHistory = async (campusId: number, productId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_merges`);

	undoMerge = async (campusId: number, mergeId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_merges/${mergeId}/_undo`, { reason });

	photoBlob = async (campusId: number, productId: number, thumb = false): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_photo`, { responseType: "blob", params: thumb ? { thumb: 1 } : {} });

	// ---- kits compuestos

	versions = async (campusId: number, productId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_versions`);

	saveVersion = async (campusId: number, productId: number, data: Record<string, any>): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_versions`, data);

	availability = async (campusId: number, productId: number, countryCode?: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_availability`, { params: countryCode ? { countryCode } : {} });

	// ---- kits por curso

	marginSales = async (campusId: number, filters: { from?: string; to?: string; band?: string }): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_sales`, { params: filters });

	riskyPrices = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_prices`);

	priceRequests = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_priceRequests`);

	approvePrice = async (campusId: number, requestId: number, price: number | null, note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_approvePrice/${requestId}`, { price, note });

	approvePrices = async (campusId: number, ids: number[], note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_approvePrices`, { ids, note });
	rejectPrice = async (campusId: number, requestId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_rejectPrice/${requestId}`, { reason });
	/** Precios por Revisar (Super Admin y Contraloría): lo capturado o aprobado por el equipo que nadie ha revisado. */
	priceReview = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_priceReview`);
	auditPrice = async (campusId: number, requestId: number, action: "ok" | "change" | "reject", price: number | null, note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_auditPrice/${requestId}`, { action, price, note });
	auditPrices = async (campusId: number, ids: number[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_auditPrices`, { ids });

	courseKits = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_courseKits`);

	saveCourseKits = async (campusId: number, courseId: number, kits: any[]): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${BASE}${campusId}/products/_courseKits/${courseId}`, { kits });

	// ---- bodegas

	warehouses = async (campusId: number, countryCode?: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/warehouses`, { params: countryCode ? { countryCode } : {} });

	stock = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/warehouses/_stock`, { params: { ...this.clean(filters), page, perPage } });

	movement = async (campusId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/warehouses/_movement`, this.form(data, photo));

	movements = async (campusId: number, productId: number, warehouseId: number | null, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_movements`, { params: { warehouseId: warehouseId || undefined, page, perPage } });

	// ---- entrega pieza por pieza

	deliveryPlan = async (campusId: number, kitId: number, warehouseId?: number | null): Promise<AxiosResponse<DeliveryPlan>> =>
		axiosInstance.get(`${BASE}${campusId}/kits/${kitId}/_deliveryPlan`, { params: warehouseId ? { warehouseId } : {} });

	/**
	 * Piece-by-piece delivery (new catalogue). Multipart since Oct 2026: items as a JSON string, note/warehouseId only when
	 * set, the student's signature (PNG data URL, mandatory) and an optional photo. See docs/CONTRATO_ENTREGA_FIRMA.md.
	 */
	registerDeliveryItems = async (campusId: number, kitId: number, items: any[], note: string, warehouseId: number | null | undefined, signature: string, photo: File | null): Promise<AxiosResponse<{ message: string; plan: DeliveryPlan; receipt: ReceiptDTO }>> => {
		const body = new FormData();
		body.append("items", JSON.stringify(items));
		if (note) body.append("note", note);
		if (warehouseId) body.append("warehouseId", String(warehouseId));
		body.append("signature", signature);
		if (photo) body.append("photo", photo);
		return axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_deliveryItems`, body);
	}
}
